import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Target, Users, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 pt-32 bg-[#0a1628] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 break-words px-4">About Service2Success</h1>
            <p className="text-lg sm:text-xl text-gray-300 px-4">
              A veteran-owned firm dedicated to connecting exceptional talent with exceptional companies
            </p>
          </div>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_skilled-talent/artifacts/cy3lq9zs_b4b93c38-0fed-430d-9f09-89c12e9095e8-1_all_22466.png"
                alt="Rich Gallagher - U.S. Navy Veteran and Founder"
                className="rounded-lg shadow-2xl"
              />
            </div>
            <div>
              <div className="inline-block bg-[#d4a574] text-[#0a1628] px-4 py-2 rounded-full text-sm font-bold mb-4">
                OUR STORY
              </div>
              <h2 className="text-4xl font-bold text-[#0a1628] mb-6">
                Founded by U.S. Navy Veteran Rich Gallagher
              </h2>
              <p className="text-lg text-gray-700 mb-4">
                Service2Success Talent Solutions was founded by Rich Gallagher, a U.S. Navy veteran and talent acquisition professional dedicated to helping both companies and job seekers succeed.
              </p>
              <p className="text-lg text-gray-700 mb-4">
                After years of recruiting in the manufacturing and technical sectors, Rich launched S2S to provide high-quality recruiting support while helping veterans translate their military experience into successful civilian careers.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                With deep expertise in engineering, manufacturing, supply chain, and skilled trades, Rich understands both the challenges employers face in finding quality talent and the unique value that veterans bring to the civilian workforce.
              </p>
              <Link to="/contact">
                <Button className="bg-[#d4a574] text-[#0a1628] hover:bg-[#c49564]">
                  Schedule a Discovery Call <ArrowRight className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#0a1628] mb-4">Our Mission</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white p-8 rounded-lg shadow-lg border-l-4 border-[#d4a574]">
                <div className="flex items-center mb-4">
                  <Award className="text-[#d4a574] mr-3" size={32} />
                  <h3 className="text-2xl font-bold text-[#0a1628]">For Veterans</h3>
                </div>
                <p className="text-gray-700">
                  We translate your operational experience into business value, helping you adapt your strengths to the civilian workforce and step confidently into your next chapter.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-lg shadow-lg border-l-4 border-[#d4a574]">
                <div className="flex items-center mb-4">
                  <Target className="text-[#d4a574] mr-3" size={32} />
                  <h3 className="text-2xl font-bold text-[#0a1628]">For Employers</h3>
                </div>
                <p className="text-gray-700">
                  We help you realize the value behind military experience and connect you with disciplined, high-integrity professionals ready to contribute from day one.
                </p>
              </div>
            </div>

            <div className="bg-[#0a1628] text-white p-8 rounded-lg text-center">
              <p className="text-2xl font-semibold mb-2">
                "Helping companies hire the right people the first time"
              </p>
              <p className="text-xl text-gray-300">
                while supporting those who served our country
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Focus Points */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-[#0a1628] text-center mb-12">What Sets Us Apart</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex items-start space-x-4">
                <CheckCircle className="text-[#d4a574] flex-shrink-0 mt-1" size={28} />
                <div>
                  <h3 className="text-xl font-bold text-[#0a1628] mb-2">Military-to-Civilian Transition Support</h3>
                  <p className="text-gray-700">
                    Specialized guidance helping veterans navigate the civilian job market and translate military skills into compelling career narratives.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <CheckCircle className="text-[#d4a574] flex-shrink-0 mt-1" size={28} />
                <div>
                  <h3 className="text-xl font-bold text-[#0a1628] mb-2">Industry Recruiting Expertise</h3>
                  <p className="text-gray-700">
                    Deep knowledge of manufacturing, engineering, supply chain, and skilled trades recruitment requirements and talent landscapes.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <CheckCircle className="text-[#d4a574] flex-shrink-0 mt-1" size={28} />
                <div>
                  <h3 className="text-xl font-bold text-[#0a1628] mb-2">Relationship-Driven Recruiting</h3>
                  <p className="text-gray-700">
                    We prioritize quality relationships over transactional placements, ensuring long-term success for both candidates and employers.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <CheckCircle className="text-[#d4a574] flex-shrink-0 mt-1" size={28} />
                <div>
                  <h3 className="text-xl font-bold text-[#0a1628] mb-2">Lean Recruiting Model</h3>
                  <p className="text-gray-700">
                    Lower overhead means better value for clients and more personalized attention throughout the entire hiring process.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Image Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <img 
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c"
              alt="Team Collaboration"
              className="w-full h-96 object-cover rounded-lg shadow-2xl mb-8"
            />
            <div className="text-center">
              <h2 className="text-3xl font-bold text-[#0a1628] mb-4">
                Built on Service, Driven by Success
              </h2>
              <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
                From the military to the manufacturing floor, from the battlefield to the boardroom, we understand the journey because we've lived it.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/services">
                  <Button size="lg" className="bg-[#d4a574] text-[#0a1628] hover:bg-[#c49564]">
                    Explore Our Services
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-2 border-[#0a1628] text-[#0a1628] hover:bg-[#0a1628] hover:text-white">
                    Get In Touch
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info Bar */}
      <section className="py-12 bg-[#0a1628] text-white">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
            <p className="text-xl text-gray-300 mb-6">
              Call to schedule a free 15-minute discovery!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href="tel:513-668-0344" className="text-2xl font-bold text-[#d4a574] hover:text-[#c49564]">
                513-668-0344
              </a>
              <span className="hidden sm:inline text-gray-500">|</span>
              <a href="mailto:rich@s2stalent.com" className="text-xl text-[#d4a574] hover:text-[#c49564]">
                rich@s2stalent.com
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
